import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  alpha,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowBack, CheckCircle, Flag, RadioButtonUnchecked, SportsScore } from '@mui/icons-material';
import { getLiveRace, LiveDriver, LiveRace as LiveRaceState, priceLiveSlip, SlipBet } from '../services/api';
import { SegmentedToggle } from '../components/SegmentedToggle';
import { americanForProb, betProbs, PricedBet, slipStats } from '../utils/betSlip';
import { legLabel, parseAmericanOdds } from '../utils/parlay';
import { BetDraft, legsFor, readChecks, readSlip, syncSlip } from '../utils/slipStore';
import { TrackShape, trackGeometry } from '../utils/trackGeometry';
import trackShapes from '../content/trackShapes.json';
import trackStartFinish from '../content/trackStartFinish.json';

// Six categorical hues (brand green first); every colored mark is also labeled, so color is never the only cue.
const HUES = ['#62BF7B', '#eb6834', '#4E9BE8', '#B07CF0', '#E2B93B', '#E86A9C'];
const CAUTION = '#E2B93B';
const LIVE_RED = '#F9696D';
// The backend reads the public feed at most every 30 s (about a lap); polling every 15 s shows each update promptly.
const POLL_MS = 15000;
// History points further apart than this were laps nobody was watching (drawn dashed).
const GAP_LAPS = 12;

const pct = (p: number) => (p >= 0.995 ? '>99%' : p < 0.005 ? '<1%' : `${Math.round(p * 100)}%`);
// Last name for short labels, skipping suffixes ("Ricky Stenhouse Jr." -> "Stenhouse").
const last = (n: string) => {
  const parts = n
    .replace(/[*#]|\([^)]*\)/g, '')
    .trim()
    .split(/\s+/);
  while (parts.length > 1 && /^(jr|sr|ii|iii|iv)\.?$/i.test(parts[parts.length - 1])) parts.pop();
  return parts[parts.length - 1];
};
const cleanName = (n: string) => n.replace(/[*#]|\([^)]*\)/g, '').trim();
const fmtAmerican = (p: number) => {
  const o = americanForProb(p);
  return o == null ? '—' : o > 0 ? `+${o}` : `${o}`;
};
const statusColor = (p: number) => (p >= 0.6 ? '#62BF7B' : p >= 0.3 ? CAUTION : '#eb6834');

type Display = 'prob' | 'odds';

/** One bet of the user's slip, ready to price live. */
interface LiveBet {
  id: string;
  label: string;
  bet: SlipBet;
  odds: number | null;
  stake: number;
}

const toLiveBets = (drafts: BetDraft[]): LiveBet[] =>
  drafts.flatMap((d) => {
    const legs = legsFor(d);
    if (!legs || legs.length === 0) return [];
    const anyLeg = !!d.anyLeg && legs.length > 1;
    const label = legs.length === 1 ? legLabel(legs[0]) : legs.map((l) => legLabel(l)).join(anyLeg ? ' or ' : ' + ');
    const stake = Number(d.stakeText);
    return [
      {
        id: d.id,
        label,
        bet: { legs, ...(anyLeg && { any: true }) },
        odds: parseAmericanOdds(d.oddsText),
        stake: Number.isFinite(stake) && stake > 0 ? stake : 0,
      },
    ];
  });

const LiveDot: React.FC = () => (
  <Box
    component="span"
    sx={{
      width: 9,
      height: 9,
      borderRadius: '50%',
      bgcolor: LIVE_RED,
      boxShadow: `0 0 0 4px ${alpha(LIVE_RED, 0.25)}`,
    }}
  />
);

const flagChip = (race: LiveRaceState) => {
  if (race.finished) return <Chip size="small" icon={<SportsScore />} label="Final" variant="outlined" />;
  const f = race.flag;
  const label =
    f === 'green'
      ? 'Green'
      : f === 'caution'
        ? 'Caution'
        : f === 'red'
          ? 'Red flag'
          : f === 'white'
            ? 'White flag'
            : f === 'checkered'
              ? 'Checkered'
              : 'Flag';
  return (
    <Chip
      size="small"
      icon={<Flag />}
      label={label}
      variant="outlined"
      sx={f === 'caution' ? { color: CAUTION, borderColor: CAUTION, '& .MuiChip-icon': { color: CAUTION } } : undefined}
      color={f === 'green' ? 'success' : f === 'red' ? 'error' : 'default'}
    />
  );
};

/** Lap counter, flag, and the race as a bar: stages, cautions (amber) and how far along we are. */
const RaceBar: React.FC<{ race: LiveRaceState; updated: number }> = ({ race, updated }) => {
  const theme = useTheme();
  const laps = race.laps || 1;
  const lap = race.lap ?? 0;
  const stages = race.stageEnds ?? [];
  const bounds = [0, ...stages.filter((s) => s < laps), laps];
  const names = bounds.slice(1).map((_, i) => (i === bounds.length - 2 ? 'Final stage' : `Stage ${i + 1}`));
  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <LiveDot />
          <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: '0.12em', color: LIVE_RED }}>
            Live
          </Typography>
        </Stack>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {race.site ?? race.track}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 400, color: 'text.secondary' }}>
          Lap <b style={{ color: theme.palette.text.primary }}>{lap}</b> / {laps}
        </Typography>
        {flagChip(race)}
        {/* Its own row on narrow screens, and a fixed-width seconds count: otherwise "updated 9s" -> "10s" can make
            the row wrap and unwrap as it ticks, and the page jumps by a line each time. */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: 'auto !important', flexBasis: { xs: '100%', sm: 'auto' }, fontVariantNumeric: 'tabular-nums' }}
        >
          {Math.max(0, laps - lap)} to go · {(race.cautions ?? []).length} caution
          {(race.cautions ?? []).length === 1 ? '' : 's'} · updated{' '}
          <Box component="span" sx={{ display: 'inline-block', minWidth: '3ch', textAlign: 'right' }}>
            {updated}
          </Box>
          s ago
        </Typography>
      </Stack>
      <Box sx={{ position: 'relative', height: 14, borderRadius: 7, bgcolor: 'action.hover', overflow: 'hidden' }}>
        <Box
          sx={{ position: 'absolute', inset: 0, width: `${(lap / laps) * 100}%`, bgcolor: alpha('#62BF7B', 0.55) }}
        />
        {(race.cautions ?? []).map(([a, b]) => (
          <Box
            key={a}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${(a / laps) * 100}%`,
              width: `${Math.max(0.5, ((b - a) / laps) * 100)}%`,
              bgcolor: CAUTION,
            }}
          />
        ))}
        {bounds.slice(1, -1).map((s) => (
          <Box
            key={s}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${(s / laps) * 100}%`,
              width: 2,
              bgcolor: 'background.paper',
            }}
          />
        ))}
      </Box>
      <Box sx={{ position: 'relative', height: 16, mt: 0.5 }}>
        {names.map((n, i) => (
          <Typography
            key={n}
            variant="caption"
            color="text.secondary"
            sx={{
              position: 'absolute',
              left: `${((bounds[i] + bounds[i + 1]) / 2 / laps) * 100}%`,
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
            }}
          >
            {n}
          </Typography>
        ))}
      </Box>
    </Paper>
  );
};

// Track drawings: the real racing line from OpenStreetMap where we have it (src/content/trackShapes.json, made by
// scripts/fetch-track-shapes.mjs), with a Google satellite image under it when REACT_APP_GOOGLE_MAPS_KEY is set;
// otherwise these hand-drawn fallbacks (Phoenix's shape, roughly; a plain oval).
const SHAPES = (trackShapes as unknown as { tracks: Record<string, TrackShape> }).tracks;
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
// Start / finish per track, set by hand: open /live?placeSF=1 and click the line on the map.
const START_FINISH = trackStartFinish as Record<string, [number, number]>;
const PLACE_SF = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('placeSF');
const PHOENIX =
  'M 150 250 L 420 250 C 520 250 560 190 545 140 C 530 95 490 80 450 76 L 300 62 C 250 58 215 66 190 74 C 120 96 70 140 80 190 C 88 230 115 250 150 250 Z';
const OVAL = 'M 170 250 L 430 250 C 560 250 560 62 430 62 L 170 62 C 40 62 40 250 170 250 Z';

/** The field on a drawing of the track: each car placed by its gap to the leader; tracked drivers in color. */
const TrackMap: React.FC<{
  race: LiveRaceState;
  color: (d: string) => string | undefined;
  lapTime: number;
}> = ({ race, color, lapTime }) => {
  const theme = useTheme();
  const ref = useRef<SVGPathElement>(null);
  const running = useMemo(() => (race.drivers ?? []).filter((d) => !d.out), [race.drivers]);
  const shape = race.site ? SHAPES[race.site] : undefined;
  const geom = useMemo(
    () => (shape ? trackGeometry(shape, MAPS_KEY, race.site ? START_FINISH[race.site] : undefined) : null),
    [shape, race.site],
  );
  // Placement mode: a click on the map gives the point's coordinates, to paste into trackStartFinish.json.
  const [placed, setPlaced] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const placeAt = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!PLACE_SF || !geom || !svg) return;
    const m = svg.getScreenCTM();
    if (!m) return;
    const pt = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
    const [lat, lng] = geom.toLatLng(pt.x, pt.y);
    const text = `"${race.site}": [${lat.toFixed(6)}, ${lng.toFixed(6)}]`;
    setPlaced(text);
    navigator.clipboard?.writeText(text).catch(() => undefined);
  };
  const [imageFailed, setImageFailed] = useState(false);
  const satellite = !!geom?.image && !imageFailed;
  const path = geom ? geom.d : (race.site ?? '').toLowerCase().startsWith('phoenix') ? PHOENIX : OVAL;
  const viewBox = geom ? geom.viewBox : '40 30 560 250';
  const mid = geom ? geom.center : { x: 315, y: 160 };
  // Dots, text and lines are sized in on-screen units, so they look the same however tightly a track is cropped
  // (the hand-drawn fallback's 560-wide frame is the reference).
  const u = geom ? geom.unit * (640 / 560) : 1;
  const [len, setLen] = useState(0);
  useLayoutEffect(() => {
    if (ref.current) setLen(ref.current.getTotalLength());
  }, [path]);
  // Each car's place relative to the leader (share of a lap behind) and its lane, worked out once per update. Cars
  // within a car-width of the one ahead move to an inner or outer lane (stable as the field goes round).
  // Only the colored cars and the leader are drawn (the rest would be noise).
  const shown = running.filter((o, k) => k === 0 || color(o.driver));
  const shownKey = shown.map((o) => `${o.driver}:${o.gap}:${o.lapsDown}`).join('|');
  const layout = useMemo(() => {
    if (!len) return [];
    const minF = (18 * u) / len;
    let prev = -1;
    let stack = 0;
    return shown.map((o) => {
      // lapped cars: the feed only gives laps down, so they're spread out of the way
      const behind = o.lapsDown > 0 ? 0.5 + (o.pos % 7) * 0.04 : o.gap / lapTime;
      stack = prev >= 0 && Math.abs(behind - prev) < minF ? stack + 1 : 0;
      prev = behind;
      return { driver: o.driver, behind, lane: stack === 0 ? 0 : (stack % 2 ? 1 : -1) * 16 * u * Math.ceil(stack / 2) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownKey, lapTime, len]);
  // The field goes round at lap speed between updates (illustrative: the feed doesn't say where on the lap anyone is).
  // The phase comes from the clock, so it doesn't drift and catches up after a background tab; a change of lap time
  // (e.g. a caution) carries on from where the field is.
  const clock = useRef({ t0: performance.now(), base: 0, lapTime });
  if (clock.current.lapTime !== lapTime) {
    const now = performance.now();
    const c = clock.current;
    clock.current = { t0: now, base: c.base + (now - c.t0) / 1000 / (c.lapTime || 1), lapTime };
  }
  const reduceMotion =
    typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [, frame] = useState(0);
  useEffect(() => {
    if (reduceMotion || !(lapTime > 0)) return;
    let raf = 0;
    const step = () => {
      frame((n) => n + 1);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [lapTime, reduceMotion]);
  const phase = reduceMotion
    ? 0
    : (clock.current.base + (performance.now() - clock.current.t0) / 1000 / (clock.current.lapTime || 1)) % 1;
  const pts: Record<string, { x: number; y: number }> = {};
  const p = ref.current;
  if (p && len) {
    const lead = 0.6; // the leader's starting spot on the drawing (the backstretch)
    layout.forEach((c) => {
      const f = (((lead + phase - c.behind) % 1) + 1) % 1;
      const q = p.getPointAtLength(f * len);
      if (!c.lane) {
        pts[c.driver] = { x: q.x, y: q.y };
        return;
      }
      const a = p.getPointAtLength(Math.max(0, f * len - 1));
      const b = p.getPointAtLength(Math.min(len, f * len + 1));
      const nx = -(b.y - a.y);
      const ny = b.x - a.x;
      const nl = Math.hypot(nx, ny) || 1;
      pts[c.driver] = { x: q.x + (nx / nl) * c.lane, y: q.y + (ny / nl) * c.lane };
    });
  }
  const leader = running[0];
  const second = running[1];
  const leadLap = running.filter((o) => o.lapsDown === 0).length;
  return (
    // overflow-anchor: none: the cars move every frame, and the browser's scroll anchoring would otherwise pick one
    // of them (or the "leads" label) as its anchor and scroll the page along with it.
    <Box sx={{ overflowAnchor: 'none' }}>
      <svg
        ref={svgRef}
        onClick={placeAt}
        viewBox={viewBox}
        width="100%"
        style={{ display: 'block', borderRadius: 8, overflowAnchor: 'none' }}
        role="img"
        aria-label="The field on track"
      >
        {satellite && (
          <image
            href={geom!.image}
            x={0}
            y={0}
            width={geom!.width}
            height={geom!.height}
            preserveAspectRatio="none"
            transform={geom!.imageTransform}
            onError={() => setImageFailed(true)}
          />
        )}
        <path
          d={path}
          fill="none"
          stroke={satellite ? 'rgba(255,255,255,0.18)' : alpha(theme.palette.text.primary, 0.08)}
          strokeWidth={(satellite ? 6 : 30) * u}
          strokeLinejoin="round"
        />
        <path
          ref={ref}
          d={path}
          fill="none"
          stroke={satellite ? 'rgba(255,255,255,0.55)' : alpha(theme.palette.text.primary, 0.22)}
          strokeWidth={1.5 * u}
          strokeDasharray={`${2 * u} ${6 * u}`}
        />
        {geom?.startFinish ? (
          <>
            <g
              transform={`translate(${geom.startFinish.x} ${geom.startFinish.y}) rotate(${(geom.startFinish.angle * 180) / Math.PI})`}
            >
              <line
                x1={0}
                y1={-16 * u}
                x2={0}
                y2={16 * u}
                stroke={satellite ? '#fff' : theme.palette.text.primary}
                strokeWidth={2.5 * u}
              />
            </g>
            {(() => {
              // upright, just outside the track (away from the infield)
              const sf = geom.startFinish!;
              const nx = -Math.sin(sf.angle);
              const ny = Math.cos(sf.angle);
              const away = (sf.x - mid.x) * nx + (sf.y - mid.y) * ny >= 0 ? 1 : -1;
              return (
                <text
                  x={sf.x + nx * away * 30 * u}
                  y={sf.y + ny * away * 30 * u + 4 * u}
                  textAnchor="middle"
                  fontSize={10 * u}
                  fontWeight={700}
                  fill={satellite ? 'rgba(255,255,255,0.9)' : theme.palette.text.secondary}
                >
                  S/F
                </text>
              );
            })()}
          </>
        ) : (
          !geom && (
            <>
              <line x1={300} y1={233} x2={300} y2={267} stroke={theme.palette.text.primary} strokeWidth={2 * u} />
              <text x={300} y={226} textAnchor="middle" fontSize={11 * u} fill={theme.palette.text.secondary}>
                Start / finish
              </text>
            </>
          )
        )}
        {/* the leader, when not one of the colored cars */}
        {running[0] && !color(running[0].driver) && pts[running[0].driver] && (
          <circle
            cx={pts[running[0].driver].x}
            cy={pts[running[0].driver].y}
            r={8 * u}
            fill={alpha(theme.palette.text.primary, 0.45)}
            stroke={theme.palette.background.paper}
            strokeWidth={2 * u}
          >
            <title>{`P1 ${cleanName(running[0].driver)}`}</title>
          </circle>
        )}
        {running
          .filter((o) => color(o.driver))
          .reverse()
          .map((o) => {
            const q = pts[o.driver];
            if (!q) return null;
            const c = color(o.driver)!;
            return (
              <g key={o.driver}>
                <title>{`P${o.pos} ${cleanName(o.driver)}`}</title>
                {o.pos === 1 && <circle cx={q.x} cy={q.y} r={17 * u} fill={alpha(c, 0.22)} />}
                <circle
                  cx={q.x}
                  cy={q.y}
                  r={11 * u}
                  fill={c}
                  stroke={theme.palette.background.paper}
                  strokeWidth={2 * u}
                />
                <text x={q.x} y={q.y + 3.5 * u} textAnchor="middle" fontSize={10 * u} fontWeight={800} fill="#0a0d0b">
                  {o.number}
                </text>
              </g>
            );
          })}
        {leader && pts[leader.driver] && (
          <text
            x={pts[leader.driver].x}
            y={pts[leader.driver].y - 22 * u}
            textAnchor="middle"
            fontSize={13 * u}
            fontWeight={800}
            fill={theme.palette.text.primary}
          >
            {`${last(leader.driver)} leads`}
          </text>
        )}
        {leader && second && (
          <>
            {satellite && (
              <rect
                x={mid.x - 120 * u}
                y={mid.y - 34 * u}
                width={240 * u}
                height={78 * u}
                rx={10 * u}
                fill="rgba(10,13,11,0.62)"
              />
            )}
            <text
              x={mid.x}
              y={mid.y - 14 * u}
              textAnchor="middle"
              fontSize={13 * u}
              fill={satellite ? 'rgba(255,255,255,0.8)' : theme.palette.text.secondary}
            >
              {`${last(leader.driver)} leads ${last(second.driver)} by`}
            </text>
            <text
              x={mid.x}
              y={mid.y + 22 * u}
              textAnchor="middle"
              fontSize={34 * u}
              fontWeight={900}
              fill={satellite ? '#fff' : theme.palette.text.primary}
            >
              {second.lapsDown > 0
                ? `${second.lapsDown} lap${second.lapsDown > 1 ? 's' : ''}`
                : `${second.gap.toFixed(1)}s`}
            </text>
            <text
              x={mid.x}
              y={mid.y + 38 * u}
              textAnchor="middle"
              fontSize={12 * u}
              fill={satellite ? 'rgba(255,255,255,0.8)' : theme.palette.text.secondary}
            >
              {`${leadLap} cars on the lead lap`}
            </text>
          </>
        )}
      </svg>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        The colored drivers (and the leader), spaced by their gap to the leader; where the field is on the lap is
        illustrative. Choose who&apos;s colored by tapping car numbers on the board.
        {PLACE_SF && (
          <Box component="span" sx={{ display: 'block', fontFamily: 'monospace', color: 'text.primary', mt: 0.5 }}>
            Start / finish placement: click the line on the map.{placed ? ` Copied: ${placed}` : ''}
          </Box>
        )}
        {geom && ' Track: © OpenStreetMap contributors.'}
        {satellite && ' Imagery: Google.'}
      </Typography>
    </Box>
  );
};

/** Win chance through the race for the tracked drivers, cautions shaded, the laps still to run left empty. */
const WinChart: React.FC<{
  race: LiveRaceState;
  tracked: string[];
  color: (d: string) => string | undefined;
  display: Display;
  w?: number;
  h?: number;
}> = ({ race, tracked, color, display, w = 900, h = 250 }) => {
  const theme = useTheme();
  const laps = race.laps || 1;
  const lap = race.lap ?? 0;
  const history = race.history ?? [];
  const W = w;
  const H = h;
  const L = 8;
  const R = w < 600 ? 118 : 170;
  const T = 14;
  const B = 26;
  const x = (l: number) => L + (l / laps) * (W - L - R);
  // Always 0-100%, whoever is shown.
  const ymax = 1;
  const y = (p: number) => T + (1 - Math.min(p, ymax) / ymax) * (H - T - B);
  const now = new Map((race.drivers ?? []).map((d) => [d.driver, d.win]));
  const ends = tracked.map((d) => ({ d, p: now.get(d) ?? 0 })).sort((a, b) => b.p - a.p);
  let prev = -1e9;
  const labelY: Record<string, number> = {};
  ends.forEach((e) => {
    const yy = Math.max(y(e.p), prev + 18);
    labelY[e.d] = yy;
    prev = yy;
  });
  const grid = [0.25, 0.5, 0.75, 1].map((g) => g * ymax);
  const ticks = (w < 600 ? [0, Math.round(laps / 2)] : [0, 100, 200, 300, 400, 500].filter((l) => l < laps)).filter(
    (l) => l < lap - laps * 0.1 || l === 0,
  );
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      style={{ display: 'block' }}
      role="img"
      aria-label="Win chance through the race"
    >
      {(race.cautions ?? []).map(([a, b]) => (
        <rect key={a} x={x(a)} y={T} width={Math.max(2, x(b) - x(a))} height={H - T - B} fill={alpha(CAUTION, 0.09)} />
      ))}
      {grid.map((g) => (
        <g key={g}>
          <line x1={L} x2={W - R} y1={y(g)} y2={y(g)} stroke={alpha(theme.palette.text.primary, 0.07)} />
          <text x={L + 2} y={y(g) - 3} fontSize={13} fill={theme.palette.text.secondary}>
            {display === 'odds' ? fmtAmerican(g) : `${Math.round(g * 100)}%`}
          </text>
        </g>
      ))}
      <rect
        x={x(lap)}
        y={T}
        width={Math.max(0, x(laps) - x(lap))}
        height={H - T - B}
        fill={alpha(theme.palette.text.primary, 0.03)}
      />
      {(race.stageEnds ?? []).map((s) => (
        <line
          key={s}
          x1={x(s)}
          x2={x(s)}
          y1={T}
          y2={H - B}
          stroke={alpha(theme.palette.text.primary, 0.18)}
          strokeDasharray="3 4"
        />
      ))}
      {ticks.map((l) => (
        <text
          key={l}
          x={x(l)}
          y={H - 6}
          textAnchor={l === 0 ? 'start' : 'middle'}
          fontSize={13}
          fill={theme.palette.text.secondary}
        >
          {l === 0 ? 'Start' : `Lap ${l}`}
        </text>
      ))}
      {tracked.map((d) => {
        // Solid where someone was watching; dashed across stretches nobody saw (the live feed has no lap history).
        const pts = history.filter((p) => p.win[d] != null).map((p) => ({ lap: p.lap, x: x(p.lap), y: y(p.win[d]) }));
        const segs: { solid: boolean; pts: typeof pts }[] = [];
        pts.forEach((p, k) => {
          const gap = k > 0 && p.lap - pts[k - 1].lap > GAP_LAPS;
          if (k === 0 || gap) {
            if (gap) segs.push({ solid: false, pts: [pts[k - 1], p] });
            segs.push({ solid: true, pts: [p] });
          } else segs[segs.length - 1].pts.push(p);
        });
        return segs.map((sg, k) =>
          sg.pts.length > 1 ? (
            <polyline
              key={`${d}${k}`}
              points={sg.pts.map((p) => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke={color(d)}
              strokeWidth={sg.solid ? 2.5 : 1.5}
              strokeDasharray={sg.solid ? undefined : '4 5'}
              strokeOpacity={sg.solid ? 1 : 0.6}
              strokeLinejoin="round"
            />
          ) : null,
        );
      })}
      {ends.map((e) => (
        <g key={e.d}>
          <circle
            cx={x(lap)}
            cy={y(e.p)}
            r={4}
            fill={color(e.d)}
            stroke={theme.palette.background.paper}
            strokeWidth={2}
          />
          <text x={x(lap) + 10} y={labelY[e.d] + 5} fontSize={14} fill={theme.palette.text.primary}>
            <tspan fontWeight={800}>{display === 'odds' ? fmtAmerican(e.p) : pct(e.p)}</tspan> {last(e.d)}
          </text>
        </g>
      ))}
    </svg>
  );
};

const Ring: React.FC<{ p: number; size?: number; color: string; children?: React.ReactNode }> = ({
  p,
  size = 120,
  color,
  children,
}) => {
  const theme = useTheme();
  const r = size / 2 - 9;
  const c = 2 * Math.PI * r;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flex: 'none' }}>
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={alpha(theme.palette.text.primary, 0.1)}
          strokeWidth={10}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={10}
          strokeLinecap="round"
          strokeDasharray={`${c * p} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <Stack sx={{ position: 'absolute', inset: 0 }} alignItems="center" justifyContent="center">
        {children}
      </Stack>
    </Box>
  );
};

/** Projected profit or loss through the race: expected profit in dollars around a $0 line, green above, orange below. */
const ProfitSpark: React.FC<{ vals: { lap: number; ev: number }[]; laps: number; cautions: [number, number][] }> = ({
  vals,
  laps,
  cautions,
}) => {
  const theme = useTheme();
  const W = 220;
  const H = 60;
  if (vals.length === 0) return <Box sx={{ height: H }} />;
  const x = (l: number) => (Math.min(l, laps || 1) / (laps || 1)) * (W - 2);
  const m = Math.max(5, ...vals.map((p) => Math.abs(p.ev))) * 1.15;
  const zero = H / 2;
  const y = (v: number) => zero - (v / m) * (H / 2 - 4);
  const area = [
    `${x(vals[0].lap)},${zero}`,
    ...vals.map((p) => `${x(p.lap)},${y(p.ev)}`),
    `${x(vals[vals.length - 1].lap)},${zero}`,
  ].join(' ');
  const lastP = vals[vals.length - 1];
  const fmt = (v: number) => `${v >= 0 ? '+' : '−'}$${Math.round(Math.abs(v))}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display: 'block' }}>
      <defs>
        <clipPath id="profit-above">
          <rect x={0} y={0} width={W} height={zero} />
        </clipPath>
        <clipPath id="profit-below">
          <rect x={0} y={zero} width={W} height={H - zero} />
        </clipPath>
      </defs>
      {cautions.map(([a, b]) => (
        <rect key={a} x={x(a)} y={0} width={Math.max(1.5, x(b) - x(a))} height={H} fill={alpha(CAUTION, 0.07)} />
      ))}
      <polygon points={area} fill={alpha('#62BF7B', 0.28)} clipPath="url(#profit-above)" />
      <polygon points={area} fill={alpha('#eb6834', 0.28)} clipPath="url(#profit-below)" />
      <line x1={0} x2={W} y1={zero} y2={zero} stroke={alpha(theme.palette.text.primary, 0.35)} />
      <text x={1} y={9} fontSize={9} fill={theme.palette.text.secondary}>
        {fmt(m)}
      </text>
      <text x={1} y={zero - 2} fontSize={9} fill={theme.palette.text.secondary}>
        $0
      </text>
      <text x={1} y={H - 1} fontSize={9} fill={theme.palette.text.secondary}>
        {fmt(-m)}
      </text>
      {vals.slice(1).map((p, k) => {
        const a = vals[k];
        // dashed across laps nobody was watching
        const gap = p.lap - a.lap > GAP_LAPS;
        return (
          <line
            key={k}
            x1={x(a.lap)}
            y1={y(a.ev)}
            x2={x(p.lap)}
            y2={y(p.ev)}
            stroke={(a.ev + p.ev) / 2 >= 0 ? '#62BF7B' : '#eb6834'}
            strokeWidth={gap ? 1.5 : 2}
            strokeDasharray={gap ? '3 4' : undefined}
            strokeOpacity={gap ? 0.6 : 1}
          />
        );
      })}
      <circle cx={x(lastP.lap)} cy={y(lastP.ev)} r={3.5} fill={lastP.ev >= 0 ? '#62BF7B' : '#eb6834'} />
    </svg>
  );
};

const Spark: React.FC<{
  vals: { lap: number; v: number }[];
  laps: number;
  cautions: [number, number][];
  color: string;
}> = ({ vals, laps, cautions, color }) => {
  const theme = useTheme();
  const W = 220;
  const H = 52;
  // The whole race left to right, 0-100% bottom to top.
  const x = (l: number) => (Math.min(l, laps || 1) / (laps || 1)) * (W - 2);
  const lo = 0;
  const hi = 1;
  const y = (v: number) => 4 + (1 - v) * (H - 8);
  if (vals.length === 0) return <Box sx={{ height: H }} />;
  const lastV = vals[vals.length - 1];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display: 'block' }}>
      {cautions.map(([a, b]) => (
        <rect key={a} x={x(a)} y={0} width={Math.max(1.5, x(b) - x(a))} height={H} fill={alpha(CAUTION, 0.07)} />
      ))}
      {lo < 0.5 && hi > 0.5 && (
        <line
          x1={0}
          x2={W}
          y1={y(0.5)}
          y2={y(0.5)}
          stroke={alpha(theme.palette.text.primary, 0.1)}
          strokeDasharray="2 4"
        />
      )}
      <text x={1} y={9} textAnchor="start" fontSize={9} fill={theme.palette.text.secondary}>
        {Math.round(hi * 100)}%
      </text>
      <text x={1} y={H - 1} textAnchor="start" fontSize={9} fill={theme.palette.text.secondary}>
        {Math.round(lo * 100)}%
      </text>
      {vals.slice(1).map((p, k) => {
        const a = vals[k];
        // dashed across laps nobody was watching (e.g. from before the race to when the page was opened)
        const gap = p.lap - a.lap > GAP_LAPS;
        return (
          <line
            key={k}
            x1={x(a.lap)}
            y1={y(a.v)}
            x2={x(p.lap)}
            y2={y(p.v)}
            stroke={color}
            strokeWidth={gap ? 1.5 : 2}
            strokeDasharray={gap ? '3 4' : undefined}
            strokeOpacity={gap ? 0.6 : 1}
          />
        );
      })}
      <circle cx={x(lastV.lap)} cy={y(lastV.v)} r={3.5} fill={color} />
    </svg>
  );
};

/** The slip during the race: the chance something pays, expected return, and each bet's live chance and drivers. */
const SlipLive: React.FC<{
  race: LiveRaceState;
  bets: LiveBet[];
  color: (d: string) => string | undefined;
}> = ({ race, bets, color }) => {
  const theme = useTheme();
  const site = race.site ?? '';
  const key = bets.map((b) => `${b.id}:${JSON.stringify(b.bet)}`).join('|');
  const priced = useQuery({
    queryKey: ['liveSlip', site, race.lap, race.updatedAt, key],
    queryFn: () => priceLiveSlip(bets.map((b) => b.bet)),
    enabled: bets.length > 0 && race.live,
    placeholderData: (prev) => prev,
    retry: false,
  });
  // The last pre-race check (Base model) for "from X%" and the starting point of the sparkline.
  const pre = useMemo(() => {
    const check = readChecks(site).base;
    if (!check) return null;
    const probs = betProbs(check.slip.outcomes, check.ids.length);
    const byId = new Map(check.ids.map((id, i) => [id, probs[i]]));
    return { byId, outcomes: check.slip.outcomes, ids: check.ids };
  }, [site]);
  const placed: PricedBet[] = bets.map((b) => ({ odds: b.odds ?? 100, stake: b.odds == null ? 0 : b.stake }));
  const outcomes = priced.data?.outcomes ?? [];
  const probs = betProbs(outcomes, bets.length);
  const hasStakes = placed.some((p) => p.stake > 0);
  const stats = outcomes.length > 0 ? slipStats(outcomes, placed) : null;
  // "Something pays": among staked bets when there are stakes, else any bet on the slip.
  const somethingPays =
    outcomes.length === 0
      ? null
      : hasStakes
        ? 1 - (stats?.pAllLose ?? 1)
        : outcomes.reduce((t, o) => t + (o.hitMask !== 0 ? o.sims : 0), 0) /
          Math.max(
            1,
            outcomes.reduce((t, o) => t + o.sims, 0),
          );
  const back = stats ? stats.staked + stats.ev : null;
  const preBack = useMemo(() => {
    if (!pre || !hasStakes) return null;
    // the check's bet order is its ids; line up stakes and odds by id
    const byId = new Map(bets.map((b, i) => [b.id, placed[i]]));
    const prePlaced = pre.ids.map((id) => byId.get(id) ?? { odds: 100, stake: 0 });
    const s = slipStats(pre.outcomes, prePlaced);
    return s.staked + s.ev;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pre, key, hasStakes]);
  // "Something pays" before the race (the last pre-race check), the sparkline's starting point.
  const preSomething = useMemo(() => {
    if (!pre) return null;
    const byId = new Map(bets.map((b, i) => [b.id, placed[i]]));
    const prePlaced = pre.ids.map((id) => byId.get(id) ?? { odds: 100, stake: 0 });
    if (hasStakes) return 1 - slipStats(pre.outcomes, prePlaced).pAllLose;
    const total = pre.outcomes.reduce((t, o) => t + o.sims, 0);
    return pre.outcomes.reduce((t, o) => t + (o.hitMask !== 0 ? o.sims : 0), 0) / Math.max(1, total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pre, key, hasStakes]);
  // "Something pays" lap by lap while the page is open (kept for this tab, so a reload doesn't lose it).
  const sparkKey = `ltb.live.spark.${site}.${race.raceId ?? ''}.${key}`;
  const [spark, setSpark] = useState<{ lap: number; v: number; ev?: number; pp?: number }[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(sparkKey) ?? '[]');
    } catch {
      return [];
    }
  });
  useEffect(() => {
    if (somethingPays == null || race.lap == null) return;
    setSpark((s) => {
      const point = {
        lap: race.lap!,
        v: somethingPays,
        ...(stats && hasStakes && { ev: stats.ev, pp: stats.pProfit }),
      };
      const next = s.length && s[s.length - 1].lap === race.lap ? s : [...s, point];
      try {
        sessionStorage.setItem(sparkKey, JSON.stringify(next));
      } catch {
        /* storage unavailable */
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [somethingPays, race.lap, sparkKey]);
  const sparkVals = preSomething != null ? [{ lap: 0, v: preSomething }, ...spark] : spark;
  // With stakes: expected profit (and the chance of finishing in profit), before the race and lap by lap.
  const preStats = useMemo(() => {
    if (!pre || !hasStakes) return null;
    const byId = new Map(bets.map((b, i) => [b.id, placed[i]]));
    return slipStats(
      pre.outcomes,
      pre.ids.map((id) => byId.get(id) ?? { odds: 100, stake: 0 }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pre, key, hasStakes]);
  const profitVals = [
    ...(preStats ? [{ lap: 0, ev: preStats.ev }] : []),
    ...spark.filter((p) => p.ev != null).map((p) => ({ lap: p.lap, ev: p.ev as number })),
  ];
  const drivers = new Map((race.drivers ?? []).map((d) => [d.driver, d]));
  const findDriver = (name: string) =>
    drivers.get(name) ?? (race.drivers ?? []).find((d) => cleanName(d.driver).toLowerCase() === name.toLowerCase());

  if (bets.length === 0)
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
          Your slip
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          No slip for {site} yet. Add bets in the Slip Builder (Simulator → Slip Builder) and they&apos;ll be tracked
          here lap by lap.
        </Typography>
        <Button component={RouterLink} to="/simulator" size="small" variant="outlined">
          Open the Simulator
        </Button>
      </Paper>
    );

  const ringP = somethingPays ?? 0;
  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          Your slip
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {bets.length} bet{bets.length === 1 ? '' : 's'}
          {hasStakes ? ` · $${stats?.staked ?? placed.reduce((t, p) => t + p.stake, 0)} staked` : ''}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Ring p={ringP} color={statusColor(ringP)}>
          {somethingPays == null ? (
            <CircularProgress size={22} />
          ) : (
            <>
              <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1 }}>
                {pct(somethingPays)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.1, px: 2 }}>
                something pays
              </Typography>
            </>
          )}
        </Ring>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {back != null && hasStakes ? (
            <>
              <Typography variant="body2" color="text.secondary">
                Expected back
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                ${Math.round(back)}{' '}
                {preBack != null && (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ color: back >= preBack ? '#62BF7B' : '#eb6834', fontWeight: 700 }}
                  >
                    {back >= preBack ? '▲' : '▼'} from ${Math.round(preBack)} before the race
                  </Typography>
                )}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Add odds and stakes in the Slip Builder to see your expected return.
            </Typography>
          )}
          {hasStakes && stats ? (
            <>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Chance of profit <b>{pct(stats.pProfit)}</b>
                {preStats && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}
                    {stats.pProfit >= preStats.pProfit ? '▲' : '▼'} from {pct(preStats.pProfit)} before the race
                  </Typography>
                )}
              </Typography>
              <ProfitSpark vals={profitVals} laps={race.laps ?? 1} cautions={race.cautions ?? []} />
              <Typography variant="caption" color="text.secondary">
                Projected profit or loss: before the race, then lap by lap while you watch
              </Typography>
            </>
          ) : (
            <>
              <Spark vals={sparkVals} laps={race.laps ?? 1} cautions={race.cautions ?? []} color={statusColor(ringP)} />
              <Typography variant="caption" color="text.secondary">
                Chance something pays: before the race, then lap by lap while you watch
              </Typography>
            </>
          )}
        </Box>
      </Stack>
      {priced.isError && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Couldn&apos;t price your slip live just now; retrying with the next update.
        </Alert>
      )}
      <Stack spacing={1.25}>
        {bets.map((b, i) => {
          const p = outcomes.length ? probs[i] : null;
          const from = pre?.byId.get(b.id);
          const missing = priced.data?.missing?.[i];
          return (
            <Box key={b.id} sx={{ p: 1.25, borderRadius: 2, border: 1, borderColor: 'divider' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 0 }} noWrap title={b.label}>
                  {b.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {b.odds != null ? (b.odds > 0 ? `+${b.odds}` : b.odds) : 'no odds'}
                  {b.stake > 0 ? ` · $${b.stake}` : ''}
                </Typography>
              </Stack>
              <Box
                sx={{
                  position: 'relative',
                  height: 8,
                  borderRadius: 4,
                  bgcolor: alpha(theme.palette.text.primary, 0.08),
                  my: 0.9,
                }}
              >
                {p != null && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${p * 100}%`,
                      borderRadius: 4,
                      bgcolor: statusColor(p),
                    }}
                  />
                )}
                {from != null && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -3,
                      bottom: -3,
                      width: 2,
                      left: `calc(${from * 100}% - 1px)`,
                      bgcolor: 'text.primary',
                      opacity: 0.6,
                    }}
                  />
                )}
              </Box>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {missing ? 'Driver not in the race' : p == null ? '…' : pct(p)}
                </Typography>
                {p != null && from != null && (
                  <Typography variant="caption" color="text.secondary">
                    {p >= from ? '▲' : '▼'} from {pct(from)} before the race
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                {b.bet.legs.map((l) => {
                  const d = findDriver(l.driver);
                  const n = l.kind === 'top_n' || l.kind === 'not_top_n' ? (l.n ?? 1) : null;
                  const opponents =
                    l.kind === 'ahead_of' ? (l.others ?? []).map((o) => ({ name: o, d: findDriver(o) })) : [];
                  // Is the leg winning right now? Top N: inside it; not top N: outside it; ahead of: ahead of everyone named.
                  const inside = !d
                    ? null
                    : d.out
                      ? false
                      : l.kind === 'top_n'
                        ? d.pos <= (n ?? 1)
                        : l.kind === 'not_top_n'
                          ? d.pos > (n ?? 1)
                          : l.kind === 'ahead_of'
                            ? opponents.every((o) => !o.d || o.d.out || d.pos < o.d.pos)
                            : null;
                  const chipSx = (name?: string) => ({
                    height: 22,
                    fontSize: 11,
                    ...(name && color(name) ? { borderLeft: `3px solid ${color(name)}` } : {}),
                    '& .MuiChip-icon': { fontSize: 14 },
                  });
                  const where = (x?: LiveDriver) => (x ? (x.out ? 'out' : `P${x.pos}`) : '');
                  return (
                    <React.Fragment key={`${l.kind}${l.driver}${l.n ?? ''}${(l.others ?? []).join(',')}`}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={inside ? <CheckCircle sx={{ color: '#62BF7B !important' }} /> : <RadioButtonUnchecked />}
                        label={
                          d
                            ? `${last(d.driver)} ${where(d)}${
                                l.kind === 'top_n' && n && n > 1
                                  ? ` · top ${n}`
                                  : l.kind === 'not_top_n'
                                    ? ` · not top ${n}`
                                    : ''
                              }`
                            : last(l.driver)
                        }
                        sx={chipSx(d?.driver)}
                      />
                      {opponents.map((o) => (
                        <Chip
                          key={o.name}
                          size="small"
                          variant="outlined"
                          label={`vs ${last(o.d?.driver ?? o.name)} ${where(o.d)}`}
                          sx={{ ...chipSx(o.d?.driver), opacity: 0.8 }}
                        />
                      ))}
                    </React.Fragment>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

/** The leaderboard (the "Table" tab of Who wins from here): position, car, driver, gap, places gained, and the
 * chance to win (and top 10) from here. */
const tiresLabel = (t: number) => (t === 4 ? '4 tires' : t === 0 ? 'fuel only' : t > 0 ? `${t} tires` : '');

/** Pit road: a green-flag cycle (who's pitted, who still has to and where they'll come out), a caution's strategy split, and the stops. */
const PitPanel: React.FC<{ race: LiveRaceState; color: (d: string) => string | undefined }> = ({ race, color }) => {
  const theme = useTheme();
  const pit = race.pit;
  if (!pit) return null;
  const byName = new Map((race.drivers ?? []).map((d) => [d.driver, d]));
  const chip = (name: string, extra?: string) => {
    const d = byName.get(name);
    const c = color(name);
    return (
      <Chip
        key={name}
        size="small"
        variant="outlined"
        label={`${d ? `#${d.number} ` : ''}${last(name)}${extra ? ` · ${extra}` : ''}`}
        sx={{ height: 22, fontSize: 11, borderLeft: `3px solid ${c ?? alpha(theme.palette.text.primary, 0.2)}` }}
      />
    );
  };
  const group = (title: string, names: string[] | undefined, extra?: (n: string) => string | undefined) =>
    names && names.length > 0 ? (
      <Box sx={{ mb: 1.25 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 700 }}>
          {title} ({names.length})
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {names.map((n) => chip(n, extra?.(n)))}
        </Stack>
      </Box>
    ) : null;
  const known = new Set([...(pit.fourTires ?? []), ...(pit.twoTires ?? []), ...(pit.noTires ?? [])]);
  const unknownTires = (pit.pittedUnder ?? []).filter((n) => !known.has(n));
  const inCycle = (pit.pitted?.length ?? 0) + (pit.toPit?.length ?? 0);
  // Each driver's best four-tire stop, fastest first.
  const bestStop = new Map<string, (typeof pit.stops)[number]>();
  pit.stops
    .filter((st) => (st.box ?? 0) > 0 && st.tires === 4)
    .forEach((st) => {
      const cur = bestStop.get(st.driver);
      if (!cur || (st.box ?? 0) < (cur.box ?? 0)) bestStop.set(st.driver, st);
    });
  const fastest = Array.from(bestStop.values())
    .sort((a, b) => (a.box ?? 0) - (b.box ?? 0))
    .slice(0, 3);
  const gainText = (g: number) => (g > 0 ? `▲${g}` : g < 0 ? `▼${-g}` : '–');
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
        Pit road
      </Typography>
      {pit.phase === 'green_cycle' && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <b>Green-flag stops underway</b> since lap {pit.cycleStartLap}: {pit.pitted?.length ?? 0} of {inCycle} have
            pitted. A stop costs about {Math.round(pit.pitLoss)}s{pit.measured ? '' : ' (estimate)'}.
          </Typography>
          <Box sx={{ height: 8, borderRadius: 4, bgcolor: alpha(theme.palette.text.primary, 0.08), mb: 1.5 }}>
            <Box
              sx={{
                width: `${inCycle ? ((pit.pitted?.length ?? 0) / inCycle) * 100 : 0}%`,
                height: '100%',
                borderRadius: 4,
                bgcolor: CAUTION,
              }}
            />
          </Box>
          {group('Still to pit · where they come out', pit.toPit, (n) => {
            const d = byName.get(n);
            return d?.cycledPos ? `P${d.pos} → P${d.cycledPos}` : undefined;
          })}
          {group('Pitted', pit.pitted, (n) => {
            const d = byName.get(n);
            return d?.cycledPos ? `P${d.cycledPos}` : undefined;
          })}
        </>
      )}
      {pit.phase === 'caution' && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <b>Caution</b> from lap {pit.cautionLap}: {pit.pittedUnder?.length ?? 0} pitted,{' '}
            {pit.stayedOut?.length ?? 0} stayed out.
          </Typography>
          {group('Stayed out · track position', pit.stayedOut)}
          {group('Four tires', pit.fourTires)}
          {group('Two tires', pit.twoTires)}
          {group('Fuel only', pit.noTires)}
          {group('Pitted', unknownTires)}
        </>
      )}
      {pit.phase === 'none' && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No pit cycle right now.
        </Typography>
      )}
      {pit.basis !== 'running' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {pit.basis === 'cycled'
            ? "Chances from here use the order once everyone's stopped."
            : 'Through the caution and the first laps after the restart, chances blend the order before the caution with the order now (pit strategy shuffles it).'}
        </Typography>
      )}
      {pit.stops.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1fr' }, gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              Latest stops
            </Typography>
            {pit.stops.slice(0, 6).map((st) => (
              <Stack
                key={`${st.driver}${st.lap}`}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ py: 0.4, borderTop: 1, borderColor: 'divider' }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ width: 34 }}>
                  L{st.lap}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}
                  noWrap
                  title={cleanName(st.driver)}
                >
                  {last(st.driver)}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}
                    {[
                      tiresLabel(st.tires),
                      st.box ? `${st.box.toFixed(1)}s stop` : '',
                      st.caution ? 'caution' : 'green',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    width: 30,
                    textAlign: 'right',
                    fontWeight: 700,
                    color: st.gained > 0 ? '#62BF7B' : st.gained < 0 ? '#eb6834' : 'text.secondary',
                  }}
                  title={`P${st.inPos} in, P${st.outPos} out`}
                >
                  {gainText(st.gained)}
                </Typography>
              </Stack>
            ))}
          </Box>
          {fastest.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                Fastest four-tire stops (each driver's best)
              </Typography>
              {fastest.map((st, i) => (
                <Stack
                  key={`${st.driver}${st.lap}f`}
                  direction="row"
                  spacing={1}
                  sx={{ py: 0.4, borderTop: 1, borderColor: 'divider' }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ width: 16 }}>
                    {i + 1}
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }} noWrap title={cleanName(st.driver)}>
                    {last(st.driver)}
                    <Typography component="span" variant="caption" color="text.secondary">
                      {' '}
                      L{st.lap}
                    </Typography>
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {st.box?.toFixed(1)}s
                  </Typography>
                </Stack>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

type PaceWindow = 'last5' | 'last10' | 'run';

/** Pace: who's fastest over recent green-flag laps, and who's closing on the car ahead. */
const PacePanel: React.FC<{ race: LiveRaceState; color: (d: string) => string | undefined }> = ({ race, color }) => {
  const theme = useTheme();
  const [win, setWin] = useState<PaceWindow>('last10');
  const drivers = (race.drivers ?? []).filter((d) => !d.out && d.pace);
  const ranked = drivers
    .filter((d) => (d.pace?.[win] ?? 0) > 0)
    .sort((a, b) => (a.pace![win] ?? 0) - (b.pace![win] ?? 0))
    .slice(0, 10);
  const best = ranked.length ? ranked[0].pace![win]! : 0;
  const worstShown = ranked.length ? ranked[ranked.length - 1].pace![win]! : 0;
  const byPos = new Map((race.drivers ?? []).filter((d) => !d.out).map((d, i, arr) => [d.driver, arr[i - 1]]));
  const battles = drivers
    .filter((d) => (d.pace?.catchIn ?? 0) > 0)
    .sort((a, b) => (a.pace!.catchIn ?? 0) - (b.pace!.catchIn ?? 0))
    .slice(0, 3);
  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          Pace
        </Typography>
        <SegmentedToggle<PaceWindow>
          size="small"
          value={win}
          onChange={setWin}
          aria-label="Pace over"
          options={[
            { value: 'last5', label: 'Last 5' },
            { value: 'last10', label: 'Last 10' },
            { value: 'run', label: 'This run' },
          ]}
        />
      </Stack>
      {battles.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            Closing in
          </Typography>
          {battles.map((d) => {
            const ahead = byPos.get(d.driver);
            return (
              <Typography key={d.driver} variant="body2" sx={{ py: 0.4, borderTop: 1, borderColor: 'divider' }}>
                <b>{last(d.driver)}</b> is catching <b>{ahead ? last(ahead.driver) : 'the car ahead'}</b> by{' '}
                {d.pace!.closing!.toFixed(2)}s a lap
                {ahead ? ` (${(d.gap - ahead.gap).toFixed(1)}s back)` : ''}: about{' '}
                <b>
                  {d.pace!.catchIn} lap{d.pace!.catchIn === 1 ? '' : 's'}
                </b>{' '}
                at this rate.
              </Typography>
            );
          })}
        </Box>
      )}
      {ranked.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Pace builds up as laps are run under green: it needs 3 or more green-flag laps since the restart (or a
          car&apos;s pit stop), while this page is open.
        </Typography>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            {race.flag === 'caution' ? 'Before the caution: best ' : 'Best '}
            {win === 'run'
              ? 'average lap over the green run'
              : `average lap over the ${win === 'last5' ? 'last 5' : 'last 10'} green laps`}
          </Typography>
          {ranked.map((d, i) => {
            const v = d.pace![win]!;
            const behind = v - best;
            const c = color(d.driver);
            const span = Math.max(0.05, worstShown - best);
            return (
              <Stack
                key={d.driver}
                direction="row"
                alignItems="center"
                spacing={1.25}
                sx={{ py: 0.5, borderTop: 1, borderColor: 'divider' }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ width: 16 }}>
                  {i + 1}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}
                  noWrap
                  title={cleanName(d.driver)}
                >
                  {cleanName(d.driver)}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}
                    P{d.pos}
                  </Typography>
                </Typography>
                <Box sx={{ width: 70, height: 6, borderRadius: 3, bgcolor: alpha(theme.palette.text.primary, 0.08) }}>
                  <Box
                    sx={{
                      width: `${Math.max(6, (1 - behind / span) * 100)}%`,
                      height: '100%',
                      borderRadius: 3,
                      bgcolor: c ?? alpha(theme.palette.text.primary, 0.35),
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ width: 58, textAlign: 'right', fontWeight: 700 }}>
                  {v.toFixed(2)}s
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ width: 44, textAlign: 'right' }}>
                  {i === 0 ? 'best' : behind < 0.005 ? '+<0.01' : `+${behind.toFixed(2)}`}
                </Typography>
              </Stack>
            );
          })}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Each number is the driver&apos;s average lap over those laps (not a single fast lap), green-flag running
            only: caution laps, restarts and pit stops are left out.
          </Typography>
        </>
      )}
    </Paper>
  );
};

const Leaderboard: React.FC<{
  race: LiveRaceState;
  color: (d: string) => string | undefined;
  display: Display;
  onDisplay: (d: Display) => void;
  compact?: boolean;
  slip?: Set<string>;
  onToggleColor?: (d: string) => void;
  onResetColors?: () => void;
}> = ({ race, color, display, onDisplay, compact, slip, onToggleColor, onResetColors }) => {
  const theme = useTheme();
  const [all, setAll] = useState(false);
  // During a green-flag pit cycle the board can show the order once everyone's stopped.
  const cycleOn = race.pit?.phase === 'green_cycle';
  const [orderBy, setOrderBy] = useState<'running' | 'cycled'>('cycled');
  const cycled = cycleOn && orderBy === 'cycled';
  const toPit = new Set(race.pit?.toPit ?? []);
  const sorted = cycled
    ? [...(race.drivers ?? [])].sort((a, b) => (a.cycledPos || 999) - (b.cycledPos || 999) || a.pos - b.pos)
    : (race.drivers ?? []);
  const drivers = sorted;
  const rows = all ? drivers : drivers.filter((d) => !d.out).slice(0, compact ? 8 : 12);
  // A short pit note per driver: during a cycle, to pit / pitted; under caution, the call they made.
  const pitTag = (d: LiveDriver): { text: string; color?: string } | null => {
    const pit = race.pit;
    if (!pit || d.out) return null;
    if (pit.phase === 'green_cycle')
      return toPit.has(d.driver) ? { text: 'to pit', color: CAUTION } : { text: 'pitted' };
    if (pit.phase === 'caution') {
      if (pit.stayedOut?.includes(d.driver)) return { text: 'stayed out', color: CAUTION };
      const st = d.stops?.[d.stops.length - 1];
      return { text: st && st.tires >= 0 ? tiresLabel(st.tires) : 'pitted' };
    }
    return null;
  };
  const topWin = Math.max(0.05, ...drivers.map((d) => d.win));
  const fmt = (p: number) => (display === 'odds' ? fmtAmerican(p) : pct(p));
  const lapsText = (n: number) => `−${n} lap${n > 1 ? 's' : ''}`;
  const lapTime = race.lapTime && race.lapTime > 0 ? race.lapTime : 30;
  const gapText = (d: LiveDriver) => {
    if (d.out) return 'Out';
    if (d.inGarage) return 'In garage';
    if (cycled && d.cycledPos) {
      if (d.cycledPos === 1) return 'Leader';
      // seconds behind once the stops play out; a lap or more of that is laps down
      const down = Math.floor((d.cycledGap ?? 0) / lapTime);
      return down > 0 ? lapsText(down) : `+${(d.cycledGap ?? 0).toFixed(1)}s`;
    }
    if (d.pos === 1) return 'Leader';
    return d.lapsDown > 0 ? lapsText(d.lapsDown) : `+${d.gap.toFixed(1)}s`;
  };
  return (
    <Box>
      {cycleOn && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <SegmentedToggle<'running' | 'cycled'>
            size="small"
            value={orderBy}
            onChange={setOrderBy}
            aria-label="Order the board by"
            options={[
              { value: 'cycled', label: 'After stops' },
              { value: 'running', label: 'Running now' },
            ]}
          />
          <Typography variant="caption" color="text.secondary">
            Pit stops underway: {cycled ? "positions once everyone's stopped" : 'positions on track now'}
          </Typography>
        </Stack>
      )}
      <Stack direction="row" spacing={1.25} sx={{ pb: 0.5, color: 'text.secondary' }}>
        <Typography variant="caption" sx={{ flex: 1, pl: '64px' }}>
          Driver
        </Typography>
        {!compact && (
          <Typography variant="caption" sx={{ width: 72, textAlign: 'right' }}>
            Gap
          </Typography>
        )}
        <Typography variant="caption" sx={{ width: 34, textAlign: 'right' }}>
          ▲▼
        </Typography>
        <Typography variant="caption" sx={{ width: compact ? 92 : 104, textAlign: 'right' }}>
          Win
        </Typography>
        {!compact && (
          <Typography variant="caption" sx={{ width: 48, textAlign: 'right' }}>
            Top 10
          </Typography>
        )}
      </Stack>
      {rows.map((d) => {
        const gained = d.start - d.pos;
        const c = color(d.driver);
        return (
          <Stack
            key={d.driver}
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{ py: 0.6, borderTop: 1, borderColor: 'divider', opacity: d.out ? 0.5 : 1 }}
          >
            <Typography
              variant="body2"
              sx={{ width: 24, fontWeight: 800, color: 'text.secondary' }}
              title={cycled && d.cycledPos ? `P${d.pos} on track now` : undefined}
            >
              {cycled && d.cycledPos ? d.cycledPos : d.pos}
            </Typography>
            <Box
              role={onToggleColor ? 'button' : undefined}
              title={onToggleColor ? (c ? 'Tap to stop coloring' : 'Tap to color this driver') : undefined}
              onClick={onToggleColor ? () => onToggleColor(d.driver) : undefined}
              sx={{
                width: 28,
                height: 20,
                flex: 'none',
                borderRadius: 1,
                display: 'grid',
                placeItems: 'center',
                fontSize: 11,
                fontWeight: 800,
                bgcolor: c ?? (slip?.has(d.driver) ? 'transparent' : alpha(theme.palette.text.primary, 0.1)),
                color: c ? '#0a0d0b' : 'text.primary',
                border: !c && slip?.has(d.driver) ? `2px solid ${theme.palette.text.primary}` : undefined,
                cursor: onToggleColor ? 'pointer' : undefined,
              }}
            >
              {d.number}
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}
              noWrap
              title={d.rated ? cleanName(d.driver) : `${cleanName(d.driver)} (not in our ratings)`}
            >
              {cleanName(d.driver)}
              {!d.rated && (
                <Typography component="span" variant="caption" color="text.secondary">
                  {' '}
                  (unrated)
                </Typography>
              )}
              {(() => {
                const tag = pitTag(d);
                return tag ? (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ ml: 0.75, fontWeight: 700, color: tag.color ?? 'text.secondary' }}
                  >
                    {tag.text}
                  </Typography>
                ) : null;
              })()}
            </Typography>
            {!compact && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ width: 72, textAlign: 'right', whiteSpace: 'nowrap' }}
              >
                {gapText(d)}
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{
                width: 34,
                textAlign: 'right',
                fontWeight: 700,
                color: gained > 0 ? '#62BF7B' : gained < 0 ? '#eb6834' : 'text.secondary',
              }}
              title="Places gained since the start"
            >
              {d.start > 0 ? (gained > 0 ? `▲${gained}` : gained < 0 ? `▼${-gained}` : '–') : ''}
            </Typography>
            <Box sx={{ width: compact ? 92 : 104, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: alpha(theme.palette.text.primary, 0.08) }}>
                <Box
                  sx={{
                    width: `${Math.min(1, d.win / topWin) * 100}%`,
                    height: '100%',
                    borderRadius: 3,
                    bgcolor: c ?? alpha(theme.palette.text.primary, 0.35),
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ width: display === 'odds' ? 46 : 30, textAlign: 'right', fontWeight: 700 }}
              >
                {fmt(d.win)}
              </Typography>
            </Box>
            {!compact && (
              <Typography variant="caption" sx={{ width: 48, textAlign: 'right' }}>
                {fmt(d.top10)}
              </Typography>
            )}
          </Stack>
        );
      })}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          ▲▼ places since the start{display === 'odds' ? ' · fair odds, no margin' : ''}
          {onToggleColor && ' · tap a car number to color it (up to 6)'}
          {onResetColors && (
            <>
              {' · '}
              <Box
                component="span"
                role="button"
                onClick={onResetColors}
                sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 700 }}
              >
                Reset
              </Box>
            </>
          )}
        </Typography>
        <Button size="small" onClick={() => setAll((a) => !a)}>
          {all ? 'Show fewer' : `All ${drivers.length}`}
        </Button>
      </Stack>
    </Box>
  );
};

/** Race live (beta): the race on now, who's likely to win from here, and how the user's slip is doing. */
export const LiveRace: React.FC = () => {
  const phone = useMediaQuery('(max-width:700px)');
  const [display, setDisplay] = useState<Display>('prob');
  const [winTab, setWinTab] = useState<'chart' | 'table'>('chart');
  const live = useQuery({
    queryKey: ['liveRace'],
    queryFn: getLiveRace,
    // Stop polling once the race is over (the final state stays on screen).
    refetchInterval: (q) => (q.state.data?.finished ? false : POLL_MS),
    refetchIntervalInBackground: false,
    retry: 1,
  });
  const race = live.data;
  const site = race?.live ? (race.site ?? '') : '';

  // The user's slip for this track: the account copy first (when signed in), then the browser's.
  const [slipVersion, setSlipVersion] = useState(0);
  useEffect(() => {
    if (!site) return;
    let canceled = false;
    syncSlip(site)
      .catch(() => undefined)
      .finally(() => !canceled && setSlipVersion((v) => v + 1));
    return () => {
      canceled = true;
    };
  }, [site]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const bets = useMemo(() => (site ? toLiveBets(readSlip(site)) : []), [site, slipVersion]);

  // Color (six hues): the slip's drivers with the most staked on them, then the likeliest winners. Every other slip
  // driver is ringed instead (more hues than six can't be told apart).
  const { tracked: autoTracked, slipDrivers } = useMemo(() => {
    const ds = race?.drivers ?? [];
    const byName = (n: string) =>
      ds.find((d) => d.driver === n || cleanName(d.driver).toLowerCase() === n.toLowerCase());
    const stake = new Map<string, number>();
    const order: string[] = [];
    bets.forEach((b) =>
      b.bet.legs.forEach((l) => {
        const d = byName(l.driver)?.driver;
        if (!d) return;
        if (!stake.has(d)) order.push(d);
        stake.set(d, (stake.get(d) ?? 0) + b.stake);
      }),
    );
    const onSlip = [...order].sort((a, b) => (stake.get(b) ?? 0) - (stake.get(a) ?? 0)); // stable: bet order on ties
    const byWin = [...ds]
      .filter((d) => !d.out)
      .sort((a, b) => b.win - a.win)
      .map((d) => d.driver);
    return {
      tracked: [...onSlip, ...byWin.filter((d) => !onSlip.includes(d))].slice(0, 6),
      slipDrivers: new Set(onSlip),
    };
  }, [race?.drivers, bets]);
  // Or the user's own pick (tap a car number on the board), remembered per track in this browser.
  const pinKey = `ltb.live.colours.${site}`; // key kept as launched, so drivers people pinned stay pinned
  const [pinned, setPinned] = useState<string[] | null>(null);
  useEffect(() => {
    try {
      const v = JSON.parse(localStorage.getItem(pinKey) ?? 'null');
      setPinned(Array.isArray(v) ? v : null);
    } catch {
      setPinned(null);
    }
  }, [pinKey]);
  const savePinned = (v: string[] | null) => {
    setPinned(v);
    try {
      if (v) localStorage.setItem(pinKey, JSON.stringify(v));
      else localStorage.removeItem(pinKey);
    } catch {
      /* storage unavailable */
    }
  };
  const inRace = new Set((race?.drivers ?? []).map((d) => d.driver));
  const tracked = pinned ? pinned.filter((d) => inRace.has(d)).slice(0, 6) : autoTracked;
  // Tap: color or clear a driver, starting from the automatic picks; with six already, the last one is replaced.
  const toggleColor = (d: string) => {
    const cur = [...tracked];
    if (cur.includes(d)) savePinned(cur.filter((x) => x !== d));
    else savePinned(cur.length >= 6 ? [...cur.slice(0, 5), d] : [...cur, d]);
  };
  const color = (d: string) => {
    const i = tracked.indexOf(d);
    return i >= 0 ? HUES[i] : undefined;
  };

  // "Updated Ns ago", ticking.
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const updated = race ? Math.max(0, Math.round((Date.now() - new Date(race.updatedAt).getTime()) / 1000)) : 0;

  // Lap time for spacing cars on the drawing: the feed's (front-runners' last laps, slower under caution), else an
  // estimate from the gaps (a lead-lap car can't be more than a lap behind).
  const lapTime = useMemo(() => {
    if (race?.lapTime && race.lapTime > 0) return race.lapTime;
    const leadLap = (race?.drivers ?? []).filter((d) => !d.out && d.lapsDown === 0);
    const maxGap = Math.max(0, ...leadLap.map((d) => d.gap));
    return Math.max(27, maxGap * 1.15);
  }, [race?.drivers, race?.lapTime]);

  const back = (
    <Button component={RouterLink} to="/simulator" size="small" startIcon={<ArrowBack />} sx={{ mb: 1 }}>
      Simulator
    </Button>
  );

  // Start / finish placement for any track, race or no race: /live?placeSF=1&track=<site>.
  const placeTrack = PLACE_SF ? new URLSearchParams(window.location.search).get('track') : null;
  if (placeTrack)
    return (
      <Box sx={{ p: phone ? 1.5 : 3, maxWidth: 900, mx: 'auto' }}>
        {back}
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
            {placeTrack}: click the start / finish line
          </Typography>
          {SHAPES[placeTrack] ? (
            <TrackMap
              race={{ live: true, site: placeTrack, drivers: [], updatedAt: new Date().toISOString() }}
              color={() => undefined}
              lapTime={30}
            />
          ) : (
            <Typography color="text.secondary">No track shape for {placeTrack}.</Typography>
          )}
        </Paper>
      </Box>
    );
  if (live.isLoading)
    return (
      <Box sx={{ p: 4, display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  if (!race)
    return (
      <Box sx={{ p: phone ? 2 : 4, maxWidth: 900, mx: 'auto' }}>
        {back}
        <Alert severity="error">Couldn&apos;t read the live race. It&apos;ll keep trying.</Alert>
      </Box>
    );
  if (!race.live)
    return (
      <Box sx={{ p: phone ? 2 : 4, maxWidth: 900, mx: 'auto' }}>
        {back}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            {race.message ?? 'No race on right now'}
          </Typography>
          <Typography color="text.secondary">
            When a race is running, this page follows it live: where everyone is, who&apos;s likely to win from here,
            and how your slip is doing. It checks again every few seconds.
          </Typography>
        </Paper>
      </Box>
    );

  const beta = (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
      Beta. Chances from here blend our pre-race ratings with the running order, which counts for more as the race goes
      on; {race.nSims?.toLocaleString()} simulated finishes, updated about once a lap. Ratings as of {race.asOf}.
    </Typography>
  );
  // Who wins from here: the chart and the leaderboard as two tabs of one box.
  const whoWins = (
    <Paper sx={{ p: phone ? 1.5 : 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          Who wins from here
        </Typography>
        <SegmentedToggle<'chart' | 'table'>
          size="small"
          value={winTab}
          onChange={setWinTab}
          aria-label="Show"
          options={[
            { value: 'chart', label: 'Chart' },
            { value: 'table', label: 'Table' },
          ]}
        />
        <Box sx={{ flex: 1 }} />
        {winTab === 'table' ? (
          <SegmentedToggle<Display>
            size="small"
            value={display}
            onChange={setDisplay}
            aria-label="Show chances as"
            options={[
              { value: 'prob', label: 'Probability' },
              { value: 'odds', label: 'Odds' },
            ]}
          />
        ) : (
          !phone && (
            <Typography variant="caption" color="text.secondary">
              Amber = cautions · gray lines = stage ends · dashed = laps nobody was watching
            </Typography>
          )
        )}
      </Stack>
      {winTab === 'chart' ? (
        <WinChart race={race} tracked={tracked} color={color} display="prob" {...(phone ? { w: 400, h: 300 } : {})} />
      ) : (
        <Leaderboard
          race={race}
          color={color}
          display={display}
          onDisplay={setDisplay}
          compact={phone}
          slip={slipDrivers}
          onToggleColor={toggleColor}
          onResetColors={pinned ? () => savePinned(null) : undefined}
        />
      )}
    </Paper>
  );
  return (
    <Box sx={{ p: phone ? 1.5 : 3, maxWidth: 1360, mx: 'auto' }}>
      {back}
      {live.isError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Couldn&apos;t update just now; showing the last update. It&apos;ll keep trying.
        </Alert>
      )}
      <RaceBar race={race} updated={updated} />
      {phone ? (
        <Stack spacing={2}>
          <SlipLive race={race} bets={bets} color={color} />
          <PitPanel race={race} color={color} />
          <Paper sx={{ p: 1.5 }}>
            <TrackMap race={race} color={color} lapTime={lapTime} />
          </Paper>
          {whoWins}
          <PacePanel race={race} color={color} />
        </Stack>
      ) : (
        <Box
          sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.45fr) minmax(0, 1fr)', gap: 2, alignItems: 'start' }}
        >
          <Stack spacing={2}>
            <Paper sx={{ p: 2, pb: 1 }}>
              <TrackMap race={race} color={color} lapTime={lapTime} />
            </Paper>
            {whoWins}
            <PitPanel race={race} color={color} />
          </Stack>
          <Stack spacing={2}>
            <SlipLive race={race} bets={bets} color={color} />
            <PacePanel race={race} color={color} />
          </Stack>
        </Box>
      )}
      {beta}
    </Box>
  );
};
